import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BlurredPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    45deg,
    var(--accent-light) 0%,
    rgba(var(--accent-rgb), 0.1) 25%,
    var(--accent-light) 50%,
    rgba(var(--accent-rgb), 0.1) 75%,
    var(--accent-light) 100%
  );
  background-size: 20px 20px;
  animation: ${fadeIn} 0.3s ease;
  filter: blur(10px);
  transform: scale(1.1);
`;

const ActualImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: ${(props) => (props.$loaded ? 1 : 0)};
  transition: opacity 0.5s ease;
  animation: ${(props) => (props.$loaded ? fadeIn : "none")} 0.5s ease;
`;

const LazyImage = ({ src, alt, placeholder, className, ...props }) => {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const [loading, setLoading] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (inView && src) {
      setLoading(true);
      const img = new Image();
      img.onload = () => {
        setLoaded(true);
        setLoading(false);
      };
      img.onerror = () => {
        setLoading(false);
      };
      img.src = src;
    }
  }, [inView, src]);

  return (
    <ImageContainer ref={imgRef} className={className} {...props}>
      {!loaded && <BlurredPlaceholder />}
      {loading && (
        <l-line-spinner size="24" stroke="2" speed="1" color="var(--accent)" />
      )}
      {inView && (
        <ActualImage
          src={src}
          alt={alt}
          $loaded={loaded}
          onLoad={() => setLoaded(true)}
        />
      )}
    </ImageContainer>
  );
};

export default LazyImage;
